import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { IJobOffer, TCareerOfferFacets, TOfferInterviewPlanRound } from '@/services/jobOffer/IJobOffers'

const resource = 'jobOffers'

const getAllPublic = (axios: IFactoryFunctionRequest) => ({ firmId }: { firmId: string }): IFactoryResponse<IJobOffer[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/public/${firmId}`,
  })
}

const getAllPublicPaginated = (axios: IFactoryFunctionRequest) => ({ firmId, query }: { firmId: string; query: object }): IFactoryResponse<IJobOffer[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/public/paginated/${firmId}`,
    query,
    immediate: false,
  })
}
/** Values the career site visitors can filter on (those of at least one listed offer) */
const publicFacets = (axios: IFactoryFunctionRequest<TCareerOfferFacets, undefined>) => ({ firmId }: { firmId: string }): IFactoryResponse<TCareerOfferFacets, undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/public/facets/${firmId}`,
  })
}
const getPaginated = (axios: IFactoryFunctionRequest) => ({ query }: { query: any }): IFactoryResponse<IJobOffer[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/paginated`,
    query,
  })
}

const onePublic = (axios: IFactoryFunctionRequest<IJobOffer, undefined>) => ({ jobId, firmId, channel, immediate = true }: { jobId: string; firmId: string; channel?: string | null; immediate?: boolean }): IFactoryResponse<IJobOffer, IJobOffer> => {
  return axios({
    method: EMethod.get,
    immediate,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/onePublic/${firmId}/${jobId}`,
    // The view is counted for the job board the visitor came from
    ...(channel ? { query: { source: channel } } : {}),
  })
}

const update = (axios: IFactoryFunctionRequest<any, undefined>) => (offerId, { data }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${offerId}`,
    data,
  })
}

const create = (axios: IFactoryFunctionRequest<any, any>) => ({ data }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/create`,
    data,
  })
}

const deleteOne = (axios: IFactoryFunctionRequest<any, any>) => ({ offerId }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${offerId}`,
  })
}

const canApply = (axios: IFactoryFunctionRequest<{ canApply: boolean; code?: string }, undefined>) => (offerId: string): IFactoryResponse<{ canApply: boolean; code?: string }> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/canApply/${offerId}`,
  })
}

/** Interview plan of an offer, its interviewers, forms and email templates populated */
const getInterviewPlan = (axios: IFactoryFunctionRequest<TOfferInterviewPlanRound[], undefined>) => (offerId: string): IFactoryResponse<TOfferInterviewPlanRound[], undefined> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${offerId}/interviewPlan`,
  })
}

/** Gives other offers of the firm this plan, in place of theirs (career site administrators) */
type TApplyInterviewPlan = { offerIds: string[], interviewPlan: TOfferInterviewPlanRound[] }
const applyInterviewPlan = (axios: IFactoryFunctionRequest<{ updated: number }, TApplyInterviewPlan>) => (data: TApplyInterviewPlan): IFactoryResponse<{ updated: number }, TApplyInterviewPlan> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/interviewPlan/apply`,
    data,
  })
}

export default (axios: AxiosInstance) => ({
  getAllPublic: getAllPublic(useAxios(axios)),
  getAllPublicPaginated: getAllPublicPaginated(useAxios(axios)),
  publicFacets: publicFacets(useAxios(axios)),
  onePublic: onePublic(useAxios(axios)),
  canApply: canApply(useAxios(axios)),
  getPaginated: getPaginated(useAxios(axios)),
  update: update(useAxios(axios)),
  create: create(useAxios(axios)),
  deleteOne: deleteOne(useAxios(axios)),
  getInterviewPlan: getInterviewPlan(useAxios(axios)),
  applyInterviewPlan: applyInterviewPlan(useAxios(axios)),
})
